import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./fleettable.css";
import { toast } from "react-toastify";
import FleetRegoTable from "./FleetRegoTable";
import LoadingSpinner from "../../component/LoadingSpinner";
import logoImage from "../../assets/logo_trucktronix.png";
import { serviceVehicleGetList } from "../../api/serviceFleetMaster";

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function FleetTable() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 200);

  const [regoPanelOpen, setRegoPanelOpen] = useState(true);
  const [controlPanelOpen, setControlPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchVehicles = async () => {
    setLoading(true);

    try {
      const dataList = await serviceVehicleGetList();

      const extracted = dataList
        .filter((v) => v?.vehicleCode)
        .sort((a, b) => a.vehicleCode.localeCompare(b.vehicleCode));

      setVehicles(extracted);
    } catch (error) {
      toast.error(error.message || "Error: Contact Trucktronix!");
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const filteredVehicles = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) => v.vehicleCode.toLowerCase().includes(q));
  }, [vehicles, debouncedSearch]);

  const handleSelectRego = (vehicle) => {
    setSelectedVehicle(vehicle);
    setRegoPanelOpen(false);
    setControlPanelOpen(true);
  };

  return (
    <div className="fleettable-page">
      {/* Header */}
      <header className="fleettable-header">
        <div className="fleettable-header-left">
          <button
            type="button"
            className={`fleettable-toggle ${regoPanelOpen ? "is-open" : "is-closed"}`}
            onClick={() => setRegoPanelOpen((v) => !v)}
            aria-expanded={regoPanelOpen}
            aria-controls="regoPanel"
          >
            <span className="fleettable-toggle-text">
              {regoPanelOpen ? "Close" : "Regos"}
            </span>
            <span className="fleettable-toggle-icon" aria-hidden="true" />
          </button>

          {!controlPanelOpen && selectedVehicle && (
            <button
              type="button"
              className="fleettable-chip"
              onClick={() => setControlPanelOpen(true)}
              title="Show search controls"
              aria-label="Show search controls"
            >
              <span className="mono">Controls</span>
              <i className="bi bi-sliders2" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="fleettable-header-right">
          <div className="fleettable-title">
            <div className="fleettable-title-main">Fleet Data</div>
            <div className="fleettable-title-sub">HaulWeigh Logs</div>
          </div>
          <img className="fleettable-logo" src={logoImage} alt="Trucktronix" />
        </div>
      </header>

      {/* Body */}
      <main className="fleettable-main">
        {/* Rego panel */}
        <section
          className="fleettable-rego-panel-card"
          aria-label="Vehicle selection"
        >
          <div
            id="regoPanel"
            className={`fleettable-accordion ${regoPanelOpen ? "is-open" : "is-closed"}`}
          >
            <div className="fleettable-accordion-inner">
              <div className="fleettable-rego-header">
                <div className="fleettable-rego-header-left">
                  <div className="fleettable-rego-title">
                    <i className="bi bi-list-ul" aria-hidden="true" />
                    <span>Vehicles</span>
                  </div>

                  <div className="fleettable-rego-meta" aria-live="polite">
                    {loading ? "Loading…" : `${filteredVehicles.length}`}
                  </div>
                </div>

                <div className="fleettable-rego-header-right">
                  <div className="fleettable-rego-search">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input
                      type="text"
                      placeholder="Search rego…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Search rego"
                    />
                    {!!searchTerm && (
                      <button
                        type="button"
                        className="fleettable-btn-clear"
                        onClick={() => setSearchTerm("")}
                        aria-label="Clear search"
                        title="Clear"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="fleettable-rego-body">
                {loading ? (
                  <div className="fleettable-loading">
                    <LoadingSpinner loadingText={"Fetching vehicles…"} />
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <div className="fleettable-inline-empty">
                    <i className="bi bi-emoji-frown" aria-hidden="true" />
                    <span>No vehicles found.</span>
                  </div>
                ) : (
                  <div className="fleettable-rego-grid" role="list">
                    {filteredVehicles.map((vehicle, idx) => {
                      const isActive = selectedVehicle?._id === vehicle._id;
                      return (
                        <button
                          key={vehicle._id}
                          type="button"
                          role="listitem"
                          className={`fleettable-rego-card ${isActive ? "active" : ""}`}
                          onClick={() => handleSelectRego(vehicle)}
                          style={{
                            animationDelay: `${Math.min(idx, 18) * 220}ms`,
                          }}
                          title={`Select ${vehicle.vehicleCode}`}
                          aria-pressed={isActive}
                        >
                          <div className="fleettable-rego-card-icon">
                            <i
                              className={`bi ${isActive ? "bi-truck-front-fill" : "bi-truck"}`}
                            />
                          </div>

                          <div className="fleettable-rego-card-info">
                            <span className="fleettable-rego-card-label">
                              Vehicle
                            </span>
                            <span
                              className={`fleettable-rego-card-value ${isActive ? "active" : ""}`}
                            >
                              {vehicle.vehicleCode}
                            </span>
                          </div>

                          <div className="fleettable-rego-card-status">
                            <i
                              className={`bi ${isActive ? "bi-check2-circle" : "bi-chevron-right"}`}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Table */}
        <section
          className="fleettable-table-area"
          aria-label="Vehicle data table"
        >
          <FleetRegoTable
            vehicle={selectedVehicle}
            controlPanelOpen={controlPanelOpen}
            setControlPanelOpen={setControlPanelOpen}
          />
        </section>
      </main>
    </div>
  );
}
